import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  type?: 'website' | 'article';
}

export function SEO({
  title,
  description,
  path = '',
  image = '/images/og-image.png',
  imageAlt = 'DKSN Business Solutions',
  imageWidth = 1200,
  imageHeight = 630,
  type = 'website'
}: SEOProps) {
  const siteUrl = 'https://dksnbsolutions.co.za';
  const fullUrl = `${siteUrl}${path}`;
  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const logoUrl = `${siteUrl}/images/dksn-logo-transparent.png`;
  // Keep titles under ~60 chars: page title + short brand suffix
  const fullTitle = `${title} | DKSN Business Solutions`;

  // Breadcrumbs for inner pages (Home > Page)
  const breadcrumbSchema =
    path && path !== '/'
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl },
            { "@type": "ListItem", "position": 2, "name": title, "item": fullUrl }
          ]
        }
      : null;

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": siteUrl,
    "name": "DKSN Business Solutions",
    "image": imageUrl,
    "logo": logoUrl,
    "url": siteUrl,
    "telephone": "+27 67 921 0514",
    "email": "sales@dksnbsolutions.co.za",
    "description": "Your trusted business solutions provider at the Mall of Mthatha. We specialize in company registration, professional branding, and quality printing services for Eastern Cape businesses.",
    "slogan": "Your local business growth partner in Mthatha",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Shop 23B, Mall of Mthatha, 33 Errol Spring Ave, Umtata Part 1",
      "addressLocality": "Mthatha",
      "addressRegion": "Eastern Cape",
      "postalCode": "5099",
      "addressCountry": "ZA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -31.5897,
      "longitude": 28.7844
    },
    "areaServed": {
      "@type": "City",
      "name": "Mthatha",
      "containedIn": "Eastern Cape"
    },
    "priceRange": "$$",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "14:00"
      }
    ],
    "sameAs": [
      "https://instagram.com/dksnbsolutions",
      "https://facebook.com/dksnbsolutions",
      "https://tiktok.com/@dksnbsolutions"
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph Tags */}
      <meta property="og:site_name" content="DKSN Business Solutions" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:locale" content="en_ZA" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Local Business Meta Tags */}
      <meta name="geo.region" content="ZA-EC" />
      <meta name="geo.placename" content="Mthatha" />
      <meta name="geo.position" content="-31.5897;28.7844" />
      <meta name="ICBM" content="-31.5897, 28.7844" />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(businessSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}
